import { DatosHistoricosResponse, VariableSeleccionada, VariableData } from '../types';
import { ChartSeries, YAxisConfig } from '../components/charts/AdvancedLineChart';

/**
 * Convierte un color hex a RGB
 */
const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
};

/**
 * Convierte RGB a hex
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + [r, g, b].map(x => {
    const hex = Math.round(x).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

/**
 * Convierte RGB a HSL
 */
const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return [h * 360, s * 100, l * 100];
};

/**
 * Convierte HSL a RGB
 */
const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  h /= 360;
  s /= 100;
  l /= 100;
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

/**
 * Genera un color naranja basado en la intensidad (luminosidad) del color primario
 * @param primaryColor - Color primario en formato hex
 * @returns Color naranja en formato hex con la misma intensidad que el color primario
 */
export const generateOrangeFromPrimary = (primaryColor: string): string => {
  const [r, g, b] = hexToRgb(primaryColor);
  const [, s, l] = rgbToHsl(r, g, b);
  
  // Color naranja tiene un matiz alrededor de 30-40 grados
  // Mantener la saturación y luminosidad del color primario para conservar la intensidad
  const orangeHue = 35; // Matiz naranja
  const orangeSaturation = Math.min(100, s * 1.1); // Aumentar ligeramente la saturación para un naranja más vibrante
  const orangeLightness = l; // Mantener la misma luminosidad/intensidad
  
  const [orangeR, orangeG, orangeB] = hslToRgb(orangeHue, orangeSaturation, orangeLightness);
  return rgbToHex(orangeR, orangeG, orangeB);
};

/**
 * Oscurece un color hex
 */
export const darkenColor = (hex: string, amount: number = 0.15): string => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const newR = Math.max(0, Math.floor(r * (1 - amount)));
  const newG = Math.max(0, Math.floor(g * (1 - amount)));
  const newB = Math.max(0, Math.floor(b * (1 - amount)));
  const toHex = (n: number) => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

/**
 * Genera un array de colores con degradado basado en un color base
 * @param count - Número de colores a generar
 * @param baseColor - Color base en formato hex
 * @param isPrimary - Si es true, genera variaciones de brillo. Si es false, genera variaciones de saturación y matiz
 * @returns Array de colores en formato hex
 */
export const generateGradientColors = (
  count: number,
  baseColor: string,
  isPrimary: boolean
): string[] => {
  if (count === 0) return [];
  if (count === 1) return [baseColor];

  const colors: string[] = [];
  const [r, g, b] = hexToRgb(baseColor);

  // Generar variaciones del color base
  for (let i = 0; i < count; i++) {
    const factor = i / (count - 1 || 1); // 0 a 1

    if (isPrimary) {
      // Para colores primarios: variar brillo y saturación
      const brightness = 0.7 + (factor * 0.3); // De 70% a 100%
      const newR = Math.min(255, r * brightness);
      const newG = Math.min(255, g * brightness);
      const newB = Math.min(255, b * brightness);
      colors.push(rgbToHex(newR, newG, newB));
    } else {
      // Para colores secundarios: variar hacia tonos complementarios
      const hueShift = factor * 30; // Hacia tonos más cálidos o fríos
      const saturation = 0.6 + (factor * 0.4); // De 60% a 100%

      // Aplicar variación de saturación
      const gray = (r + g + b) / 3;
      let newR = gray + (r - gray) * saturation;
      let newG = gray + (g - gray) * saturation;
      let newB = gray + (b - gray) * saturation;

      // Aplicar ligero cambio de matiz
      const shift = hueShift / 360;
      if (shift > 0) {
        // Hacia tonos más cálidos (más rojo/amarillo)
        newR = Math.min(255, newR + shift * 20);
        newG = Math.min(255, newG + shift * 10);
      } else {
        // Hacia tonos más fríos (más azul)
        newB = Math.min(255, newB + Math.abs(shift) * 20);
      }

      colors.push(rgbToHex(newR, newG, newB));
    }
  }

  return colors;
};

/**
 * Genera colores simples usando variaciones de oscurecimiento
 * Útil para casos donde no se necesita un degradado complejo
 */
export const generateSimpleColors = (
  count: number,
  baseColor: string,
  _isPrimary: boolean
): string[] => {
  if (count === 0) return [];
  if (count === 1) return [baseColor];

  const colors: string[] = [];
  const variations = [0, 0.1, 0.2, 0.15, 0.25];

  for (let i = 0; i < count; i++) {
    const variation = variations[i % variations.length];
    colors.push(variation > 0 ? darkenColor(baseColor, variation) : baseColor);
  }

  return colors;
};

/**
 * Prepara las series del gráfico desde variables seleccionadas y datos históricos
 * @param datos - Datos históricos de la API
 * @param variables - Variables seleccionadas para mostrar
 * @param primaryColor - Color base para el eje primario
 * @param _secondaryColor - Color base para el eje secundario (no usado directamente)
 * @param useGradient - Si es true, usa degradado de colores. Si es false, usa colores simples
 * @param colorMap - Mapa opcional de colores por variable (key: `${variable_id}-${sensor_id}`)
 * @param getSensorNombre - Función opcional para obtener el nombre del sensor si no está en la variable
 * @param agregacionPrimario - Tipo de agregación para el eje primario (solo se muestra si hay intervalo)
 * @param agregacionSecundario - Tipo de agregación para el eje secundario (solo se muestra si hay intervalo)
 * @param intervalo - Intervalo de agregación (si hay intervalo, se muestra la agregación en las etiquetas)
 * @returns Array de series para el gráfico
 */
export const prepareChartSeries = (
  datos: DatosHistoricosResponse | null,
  variables: VariableSeleccionada[],
  primaryColor: string,
  _secondaryColor: string,
  useGradient: boolean = true,
  colorMap?: Record<string, string>,
  getSensorNombre?: (variable: VariableSeleccionada) => string | null,
  agregacionPrimario?: string,
  agregacionSecundario?: string,
  intervalo?: string
): ChartSeries[] => {
  if (!datos || !datos.variables || variables.length === 0) {
    return [];
  }

  const series: ChartSeries[] = [];

  // Separar variables por eje para aplicar degradado
  const primaryVars: VariableSeleccionada[] = [];
  const secondaryVars: VariableSeleccionada[] = [];

  variables.forEach((selectedVar) => {
    const variableData = datos.variables.find(
      (v) => v.variable_id === selectedVar.variable_id && v.sensor_id === selectedVar.sensor_id
    );

    if (variableData && variableData.datos.length > 0) {
      if (selectedVar.eje === 'primary') {
        primaryVars.push(selectedVar);
      } else if (selectedVar.eje === 'secondary') {
        secondaryVars.push(selectedVar);
      }
    }
  });

  // Generar colores con degradado o simples para cada eje
  const generateColors = useGradient ? generateGradientColors : generateSimpleColors;
  const primaryColors = generateColors(primaryVars.length, primaryColor, true);
  
  // Para el eje secundario, generar un color naranja basado en el color primario
  // y crear un degradado sobre ese color naranja
  const orangeBaseColor = generateOrangeFromPrimary(primaryColor);
  const secondaryColors = generateColors(secondaryVars.length, orangeBaseColor, false);

  // Crear series con colores degradados
  primaryVars.forEach((selectedVar, index) => {
    const variableData = datos.variables.find(
      (v) => v.variable_id === selectedVar.variable_id && v.sensor_id === selectedVar.sensor_id
    );

    if (variableData && variableData.datos.length > 0) {
      const variableKey = `${selectedVar.variable_id}-${selectedVar.sensor_id}`;
      const color = selectedVar.color || 
        (colorMap && colorMap[variableKey]) || 
        primaryColors[index] || 
        primaryColor;
      
      // Obtener nombre del sensor
      let sensorNombre = selectedVar.sensor_nombre;
      if (!sensorNombre && getSensorNombre) {
        sensorNombre = getSensorNombre(selectedVar) || null;
      }
      
      // Construir etiqueta con agregación si hay intervalo
      let label = `${sensorNombre || 'Sensor'} - ${selectedVar.variable_nombre}`;
      if (intervalo && intervalo !== 'NONE' && agregacionPrimario) {
        const aggLabel = agregacionPrimario === 'AVG' ? 'avg' : agregacionPrimario === 'MAX' ? 'max' : 'min';
        label = `${label} (${aggLabel})`;
      }
      
      const dataKey = `${selectedVar.variable_id}_${selectedVar.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');

      series.push({
        key: dataKey,
        label,
        color,
        unidad: selectedVar.variable_unidad,
        yAxisId: 'primary',
        dataKey,
      });
    }
  });

  secondaryVars.forEach((selectedVar, index) => {
    const variableData = datos.variables.find(
      (v) => v.variable_id === selectedVar.variable_id && v.sensor_id === selectedVar.sensor_id
    );

    if (variableData && variableData.datos.length > 0) {
      const variableKey = `${selectedVar.variable_id}-${selectedVar.sensor_id}`;
      const color = selectedVar.color || 
        (colorMap && colorMap[variableKey]) || 
        secondaryColors[index] || 
        orangeBaseColor;
      
      // Obtener nombre del sensor
      let sensorNombre = selectedVar.sensor_nombre;
      if (!sensorNombre && getSensorNombre) {
        sensorNombre = getSensorNombre(selectedVar) || null;
      }
      
      // Construir etiqueta con agregación si hay intervalo
      let label = `${sensorNombre || 'Sensor'} - ${selectedVar.variable_nombre}`;
      if (intervalo && intervalo !== 'NONE' && agregacionSecundario) {
        const aggLabel = agregacionSecundario === 'AVG' ? 'avg' : agregacionSecundario === 'MAX' ? 'max' : 'min';
        label = `${label} (${aggLabel})`;
      }
      
      const dataKey = `${selectedVar.variable_id}_${selectedVar.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');

      series.push({
        key: dataKey,
        label,
        color,
        unidad: selectedVar.variable_unidad,
        yAxisId: 'secondary',
        dataKey,
      });
    }
  });

  return series;
};

/**
 * Formatea un valor numérico para los ejes Y, usando notación científica si el rango es muy pequeño
 * @param value - Valor a formatear
 * @param min - Valor mínimo del dominio del eje (opcional)
 * @param max - Valor máximo del dominio del eje (opcional)
 * @returns String formateado
 */
export const formatYAxisTick = (value: number, min?: number, max?: number): string => {
  if (typeof value !== 'number' || !isFinite(value)) {
    return String(value);
  }

  // Si tenemos min y max, calcular el rango
  if (min !== undefined && max !== undefined && isFinite(min) && isFinite(max)) {
    const range = Math.abs(max - min);
    
    // Si el rango es menor a 0.01, usar notación científica
    if (range < 0.01 && range > 0) {
      // Formatear en notación científica con 1 decimal
      // Ejemplo: 2E-3, 4E-3, -1.5E-2
      const formatted = value.toExponential(1);
      return formatted;
    }
  }

  // Para valores grandes, usar formato abreviado (k, M, etc.)
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }

  // Para valores normales, usar formato decimal estándar
  return value.toFixed(2);
};

/**
 * Crea un formatter para los ticks del eje Y basado en el dominio
 * @param min - Valor mínimo del dominio del eje (opcional)
 * @param max - Valor máximo del dominio del eje (opcional)
 * @returns Función formatter
 */
export const createYAxisTickFormatter = (min?: number, max?: number): ((value: number) => string) => {
  return (value: number) => formatYAxisTick(value, min, max);
};

/**
 * Prepara la configuración de ejes Y desde variables seleccionadas
 * @param variables - Variables seleccionadas
 * @returns Array de configuración de ejes Y
 */
export const prepareChartYAxes = (variables: VariableSeleccionada[]): YAxisConfig[] => {
  if (!variables || variables.length === 0) {
    return [];
  }

  const axes: YAxisConfig[] = [];

  // Eje primario
  const primaryVars = variables.filter((v) => v.eje === 'primary');
  if (primaryVars.length > 0) {
    const unidadesSet = new Set(primaryVars.map((v) => v.variable_unidad).filter(Boolean));
    const unidadesArray = Array.from(unidadesSet);

    axes.push({
      id: 'primary',
      label: unidadesArray.length === 1 && unidadesArray[0]
        ? unidadesArray[0]
        : unidadesArray.length > 1
        ? `${unidadesArray[0]} (y más)`
        : 'Eje Primario',
      orientation: 'left',
      tickFormatter: createYAxisTickFormatter(),
    });
  }

  // Eje secundario
  const secondaryVars = variables.filter((v) => v.eje === 'secondary');
  if (secondaryVars.length > 0) {
    const unidadesSet = new Set(secondaryVars.map((v) => v.variable_unidad).filter(Boolean));
    const unidadesArray = Array.from(unidadesSet);

    axes.push({
      id: 'secondary',
      label: unidadesArray.length === 1 && unidadesArray[0]
        ? unidadesArray[0]
        : unidadesArray.length > 1
        ? `${unidadesArray[0]} (y más)`
        : 'Eje Secundario',
      orientation: 'right',
      tickFormatter: createYAxisTickFormatter(),
    });
  }

  return axes;
};



